// packages/core/src/learning/schools/minimalism.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Minimalism". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: courtney-carver, cait-flanders,
//                     vicki-robin, jacob-lund-fisker.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const MINIMALISM_EXHIBIT: SchoolExhibitEntry = {
	id: 'minimalism',
	tagline: 'Minimalism believes less can give you more room to live.',
	worldviewSummary:
		'Minimalism is paying attention to how clutter, consumption, noise, and constant wanting can drain money, attention, and peace. This lens protects you from building a life that looks full but feels crowded. It accepts a real tradeoff: owning less and wanting less can create freedom, but it can also become rigid if simplicity turns into identity or judgment. The blind spot is that minimalism can ignore income, housing, family, culture, and real needs when it becomes too aesthetic or too pure. At its best, minimalism is not about empty rooms. It is about removing what keeps stealing your attention, money, and energy.',
	coreBeliefs: [
		'Minimalism believes every object, habit, and commitment has a carrying cost.',
		'Minimalism believes wanting less can be as powerful as earning more.',
		'Minimalism believes clutter is not only physical. It can be financial, digital, emotional, and social.',
		'Minimalism believes freedom often comes from reducing what you have to maintain.',
		'Minimalism believes simplicity should protect attention, not become a performance.',
		'Minimalism believes the goal is enough, not emptiness.',
	],
	warnsAgainst: [
		'Buying more because your life feels emotionally crowded.',
		'Keeping things, subscriptions, commitments, and habits that quietly drain attention.',
		'Using simplicity as a status signal.',
		'Confusing minimalism with deprivation, purity, or judging other people’s needs.',
	],
	helps: 'This lens helps people who feel financially, mentally, or emotionally crowded by too much stuff, too many commitments, or too many defaults. It may not fit when simplicity becomes rigid, aesthetic, judgmental, or disconnected from family needs and real life constraints.',
	figures: [],
	canon: [
		{
			title: 'Soulful Simplicity',
			author: 'Courtney Carver',
			oneLineIdea:
				'A simpler life can make room for health, attention, and what actually matters.',
		},
		{
			title: 'The Year of Less',
			author: 'Cait Flanders',
			oneLineIdea:
				'Buying less can expose which purchases were filling stress, boredom, or identity gaps.',
		},
		{
			title: 'Goodbye Things',
			author: 'Fumio Sasaki',
			oneLineIdea:
				'Reducing possessions can reduce mental load and make ordinary life feel lighter.',
		},
		{
			title: 'Essentialism',
			author: 'Greg McKeown',
			oneLineIdea:
				'Choosing fewer priorities can protect your best energy from constant noise.',
		},
		{
			title: 'Voluntary simplicity',
			oneLineIdea:
				'A lower need life can create more freedom when it is chosen with care.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-minimalism',
	debateIds: [],
	applyActions: [
		{
			label: 'See whether lower needs change your freedom path',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Check how much breathing room simplicity could create',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name what is costing more than money',
			action: {
				type: 'noToolAction',
				label: 'Find the hidden cost',
				prompt: 'Write one object, habit, subscription, or commitment that costs you money, attention, energy, or peace.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Minimalism is not about owning nothing. It is about owing less of your life to stuff.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of clutter is not space. It is attention.',
			kind: 'hook',
		},
		{
			line: 'What did you remove from your life that gave you more peace than expected?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what they stopped buying once they knew what enough felt like.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users test whether lower needs create real freedom, not just a cleaner room.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how wanting less can change the math without making life smaller.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does minimalism help, and where does it turn into another status game?',
			kind: 'interview-question',
		},
		{
			line: 'A crowded life can still look successful from the outside.',
			kind: 'hook',
		},
	],
	revision: 1,
};
